import type { CSSProperties } from 'react';
import type { Environment, Project } from '@hangar/shared';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { ForksField, PatternsField, ProtectionField } from './EnvironmentGuardrailFields';
import { KindField, NameField, OrderField, SlugField } from './EnvironmentIdentityFields';
import { formTitle } from './environmentFormModel';
import { useEnvironmentForm, type EnvironmentFormApi } from './useEnvironmentForm';

interface Props {
  project: Project;
  /** Absent: a new environment (`POST /projects/{id}/environments`). */
  environment?: Environment;
  onDone: (saved?: Environment) => void;
}

/**
 * Design 28 · "Edit environment": inline under its row (or under the table for a new one). Name and slug, color,
 * protection, promotion order, allowed branch patterns as chips, forks and untrusted refs. The guardrail rules of
 * @hangar/shared are checked in the field before the API checks them again: a protected environment lists at least one
 * branch, none matching every branch, and always blocks untrusted refs.
 */
export function EnvironmentForm(props: Props) {
  const form = useEnvironmentForm(props);
  const { env, view } = form;
  return (
    <form
      data-testid={env ? `env-form-${env.slug}` : 'env-form-new'}
      onSubmit={(e) => void form.onSubmit(e)}
      noValidate
      aria-label={formTitle(env)}
      className="fade-in flex flex-col gap-4 border-l-2 py-4 pl-4 pr-4 [border-left-color:var(--pill)] sm:pl-5 sm:pr-5"
      style={{ '--pill': view.shownColor } as CSSProperties}
    >
      <FormHeader title={formTitle(env)} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.6fr)]">
        <NameField form={form} />
        <SlugField form={form} />
        <OrderField form={form} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <KindField form={form} />
        <ProtectionField form={form} />
      </div>
      <PatternsField form={form} />
      <ForksField form={form} />
      <ProblemAlert error={form.problem} testId="env-form-problem" />
      <FormFooter form={form} />
    </form>
  );
}

function FormHeader({ title }: { title: string }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b pb-3">
      <Kicker className="text-[13px]">
        <Icon name="tune" size={18} className="text-[color:var(--pill)]" />
        <span className="min-w-0 break-all">{title}</span>
      </Kicker>
      <span className="text-xs text-subtle">Applies to every future build and release</span>
    </div>
  );
}

function FormFooter({ form }: { form: EnvironmentFormApi }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
      <Button variant="secondary" data-testid="env-form-cancel" onClick={form.onCancel}>
        Cancel
      </Button>
      <Button type="submit" data-testid="env-form-submit" loading={form.busy}>
        {form.env ? 'Save environment' : 'Add environment'}
      </Button>
    </div>
  );
}
